import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Mic, Sparkles, Play, Pause, Download, Plus, Loader2, Lightbulb,
  Radio, Megaphone, Clock, Volume2, Upload, Copy as CopyIcon, SlidersHorizontal,
  Radio as RadioIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import VoiceRecorderModal from "@/components/VoiceRecorderModal";
import s from "./ArtistDrops.module.css";

const TEMPLATES = [
  { id: "classic",  label: "Classic station ID",  preview: '"Hey, it\'s [Voice Persona] and you\'re listening to [Station]!"', script: "Hey, it's {VoicePersona} and you're listening to {Station}!" },
  { id: "lockin",   label: "Station lock-in",    preview: '"This is [Voice Persona] — stay locked to [Station]..."',         script: "This is {VoicePersona} — stay locked to {Station} for the best R&B all day, every day." },
  { id: "smooth",   label: "Smooth intro",       preview: '"What\'s up, it\'s [Voice Persona]. You\'re tuned in to..."',     script: "What's up, it's {VoicePersona}. You're tuned in to the smoothest station on the dial — {Station}." },
  { id: "hype",     label: "Hype drop",          preview: '"[Voice Persona] here. Only one place to be..."',                 script: "{VoicePersona} here. Only one place to be — {Station}. Let's go!" },
];

const SETTINGS_OPTIONS = {
  tone: ["Energetic", "Smooth", "Authoritative", "Friendly", "Hype"],
  speed: ["Normal", "Slightly fast", "Slow", "Fast"],
  musicBed: ["None", "Smooth Jazz", "R&B Groove", "Hip-Hop Beat", "Gospel Lift", "Caribbean Vibe"],
  format: ["MP3 320kbps", "WAV 24-bit", "MP3 128kbps", "FLAC"],
};

const DROP_TYPES = [
  { id: "namedrop",  icon: Mic,         label: "Station voice drop" },
  { id: "stationid", icon: Radio,       label: "Station ID" },
  { id: "sweeper",   icon: Megaphone,   label: "Sweeper / promo" },
  { id: "timecheck", icon: Clock,       label: "Time check drop" },
];

const TIPS = [
  { bold: "Keep scripts under 10 seconds",  rest: " — shorter drops get more airplay" },
  { bold: "Use {VoicePersona} placeholder", rest: " — swap between your station personas without regenerating" },
  { bold: "Generate 3–5 variations",         rest: " — rotate them to avoid listener fatigue" },
  { bold: "Add a music bed",                 rest: " — gives the drop a professional broadcast feel" },
  { bold: "Cloning your own talent?",        rest: " — you'll confirm you have their consent before the clone is created" },
];

function timeAgo(d) {
  const sec = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

// Web Audio API: mix voice over a music bed at -12dB (≈ gain 0.25)
async function mixWithBed(voiceBlob, bedUrl) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx || !bedUrl) return voiceBlob;
  try {
    const ctx = new AudioCtx();
    const [voiceBuf, bedBuf] = await Promise.all([
      voiceBlob.arrayBuffer().then((b) => ctx.decodeAudioData(b)),
      fetch(bedUrl).then((r) => r.arrayBuffer()).then((b) => ctx.decodeAudioData(b)),
    ]);
    const sampleRate = voiceBuf.sampleRate;
    const channels = Math.max(voiceBuf.numberOfChannels, 2);
    const length = voiceBuf.length;
    const off = new OfflineAudioContext(channels, length, sampleRate);

    const vs = off.createBufferSource(); vs.buffer = voiceBuf; vs.connect(off.destination);
    const bs = off.createBufferSource(); bs.buffer = bedBuf;
    const bg = off.createGain(); bg.gain.value = 0.25; bs.connect(bg); bg.connect(off.destination);

    vs.start(0); bs.start(0);
    const rendered = await off.startRendering();
    try { ctx.close(); } catch { /* ignore close errors */ }
    return audioBufferToWavBlob(rendered);
  } catch (e) {
    // Mixing failed — fall back to bare voice so we don't block UX
    return voiceBlob;
  }
}

function audioBufferToWavBlob(buffer) {
  const numCh = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numCh * 2 + 44;
  const out = new ArrayBuffer(length);
  const view = new DataView(out);
  let offset = 0;
  const writeStr = (str) => { for (let i = 0; i < str.length; i++) view.setUint8(offset++, str.charCodeAt(i)); };
  const write32 = (v) => { view.setUint32(offset, v, true); offset += 4; };
  const write16 = (v) => { view.setUint16(offset, v, true); offset += 2; };
  writeStr("RIFF"); write32(length - 8); writeStr("WAVE");
  writeStr("fmt "); write32(16); write16(1); write16(numCh);
  write32(sampleRate); write32(sampleRate * numCh * 2); write16(numCh * 2); write16(16);
  writeStr("data"); write32(length - 44);

  const channels = [];
  for (let i = 0; i < numCh; i++) channels.push(buffer.getChannelData(i));
  let sampleIdx = 0;
  while (sampleIdx < buffer.length) {
    for (let c = 0; c < numCh; c++) {
      const s = Math.max(-1, Math.min(1, channels[c][sampleIdx]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      offset += 2;
    }
    sampleIdx++;
  }
  return new Blob([out], { type: "audio/wav" });
}

// Deterministic waveform heights per drop id so re-renders don't jitter
function waveHeights(id, count) {
  const out = [];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  for (let i = 0; i < count; i++) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    out.push(20 + (h % 75));
  }
  return out;
}

export default function ArtistDrops() {
  const { user } = useAuth();
  const stationDefault = user?.station_name || "Seductive Vibes Radio";

  // ── Server-loaded voice presets ──────────────────────────────────
  const [voices, setVoices] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [customVoiceId, setCustomVoiceId] = useState(null);
  const [customVoiceName, setCustomVoiceName] = useState("");
  const [cloning, setCloning] = useState(false);
  const cloneInputRef = useRef(null);

  // ── Script / voice / settings state ──────────────────────────────
  const [scriptTab, setScriptTab] = useState("templates");
  const [activeTemplate, setActiveTemplate] = useState("classic");
  const [script, setScript] = useState(TEMPLATES[0].script);
  const [selectedVoice, setSelectedVoice] = useState("marcus");
  const [settings, setSettings] = useState({
    tone: "Energetic", speed: "Normal", musicBed: "None", format: "MP3 320kbps",
  });

  // ── Right panel ──────────────────────────────────────────────────
  const [personaName, setPersonaName] = useState("");
  const [stationName, setStationName] = useState(stationDefault);
  const [dropType, setDropType] = useState("namedrop");
  const [variations, setVariations] = useState(3);

  // ── Generation + history ─────────────────────────────────────────
  const [generating, setGenerating] = useState(false);
  const [drops, setDrops] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const audioRefs = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const fetchVoices = () => api.get("/artist-drops/voices");
      try {
        let r;
        try { r = await fetchVoices(); }
        catch {
          // transient 502s during backend reloads — retry once before erroring
          await new Promise((res) => setTimeout(res, 1500));
          r = await fetchVoices();
        }
        setVoices(r.data?.presets || []);
        setConfigured(!!r.data?.configured);
        if (r.data?.cloned_voice) {
          setCustomVoiceId(r.data.cloned_voice.voice_id);
          setCustomVoiceName(r.data.cloned_voice.name);
        }
      } catch (e) {
        toast.error("Could not load voice presets — refresh the page to retry");
      }
    })();
  }, []);

  // ── Template handling ────────────────────────────────────────────
  const pickTemplate = (tpl) => {
    setActiveTemplate(tpl.id);
    setScript(tpl.script);
    setScriptTab("templates");
  };

  // ── Voice clone (Your Station Voice slot) — consent-gated ────────
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showCloneRecorder, setShowCloneRecorder] = useState(false);
  const onPickCustom = () => {
    setSelectedVoice("custom");
    if (!customVoiceId) { setConsentChecked(false); setConsentOpen(true); }
  };
  const openReupload = (e) => {
    e.stopPropagation();
    setSelectedVoice("custom");
    setConsentChecked(false);
    setConsentOpen(true);
  };
  const onCloneFile = async (file) => {
    if (!file) return;
    setCloning(true);
    const fd = new FormData();
    fd.append("name", `${user?.email?.split("@")[0] || "station"} voice`);
    fd.append("description", "Cloned station voice for BroadcastFlow AI voice drops");
    fd.append("consent_attestation", "true");
    fd.append("sample", file);
    try {
      const r = await fetch(`${import.meta.env?.VITE_API_URL || ""}/api/artist-drops/voices/clone`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      // Fallback to axios with proper headers if fetch path mis-resolves
      let voice_id, name;
      if (r.ok) ({ voice_id, name } = await r.json());
      else throw new Error(await r.text());
      setCustomVoiceId(voice_id);
      setCustomVoiceName(name);
      setSelectedVoice("custom");
      toast.success("Station voice cloned successfully");
    } catch (e) {
      // Try axios path (some setups use cookie-based auth that fetch above doesn't replay)
      try {
        const fd2 = new FormData();
        fd2.append("name", `${user?.email?.split("@")[0] || "station"} voice`);
        fd2.append("description", "Cloned station voice for BroadcastFlow AI voice drops");
        fd2.append("consent_attestation", "true");
        fd2.append("sample", file);
        const r2 = await api.post("/artist-drops/voices/clone", fd2);
        setCustomVoiceId(r2.data.voice_id);
        setCustomVoiceName(r2.data.name);
        setSelectedVoice("custom");
        toast.success("Station voice cloned successfully");
      } catch (err) {
        toast.error(err?.response?.data?.detail || err?.message || "Voice clone failed");
      }
    } finally {
      setCloning(false);
    }
  };

  // ── Resolve voice id ─────────────────────────────────────────────
  const resolveVoiceId = () => {
    if (selectedVoice === "custom") return customVoiceId;
    return voices.find((v) => v.id === selectedVoice)?.elevenlabs_id;
  };

  // ── Stop all audio ───────────────────────────────────────────────
  const stopAll = () => {
    Object.values(audioRefs.current).forEach((a) => { try { a.pause(); } catch { /* noop */ } });
    setPlayingId(null);
  };

  // ── Generate (1..variations sequentially) ────────────────────────
  const handleGenerate = async () => {
    const voice_id = resolveVoiceId();
    if (!voice_id) {
      if (selectedVoice === "custom") {
        toast.error("Upload a sample first to use the custom voice");
        cloneInputRef.current?.click();
      } else {
        toast.error("Pick a voice first");
      }
      return;
    }
    if (!script.trim()) { toast.error("Add a script first"); return; }
    if (!configured) {
      toast.error("ElevenLabs is not configured. Ask an admin to set ELEVENLABS_API_KEY.");
      return;
    }

    setGenerating(true);
    const N = Math.max(1, Math.min(10, variations));
    let okCount = 0;
    let lastErr = null;
    for (let i = 0; i < N; i++) {
      try {
        const voice = voices.find((v) => v.id === selectedVoice) || { name: customVoiceName || "Your Station Voice" };
        const res = await api.post("/artist-drops/generate", {
          script,
          artist_name: personaName,
          station_name: stationName,
          voice_id,
          tone: settings.tone,
          speed: settings.speed,
          format: settings.format,
        }, { responseType: "blob" });
        let blob = res.data;
        // Music bed mixing (best-effort — falls back to bare voice on failure)
        if (settings.musicBed && settings.musicBed !== "None") {
          // For V1 we use a packaged sample URL per bed type; future iteration: pick from imaging library
          const bedUrl = `/beds/${settings.musicBed.toLowerCase().replace(/[^a-z]/g, "")}.mp3`;
          blob = await mixWithBed(blob, bedUrl);
        }
        const url = URL.createObjectURL(blob);
        // Probe duration
        const dur = await new Promise((resolve) => {
          const a = new Audio(url);
          a.onloadedmetadata = () => resolve(a.duration || 0);
          a.onerror = () => resolve(0);
        });
        const id = `${Date.now()}_${i}`;
        setDrops((prev) => [{
          id, voiceId: selectedVoice, voiceName: voice.name,
          script: script
            .replace(/{VoicePersona}/g, personaName || "your station voice")
            .replace(/{Artist}/g, personaName || "your station voice")
            .replace(/{Station}/g, stationName),
          audioUrl: url, audioBlob: blob,
          duration: dur ? `0:${String(Math.round(dur)).padStart(2, "0")}` : "0:00",
          durationSec: Math.round(dur || 0),
          format: settings.format,
          createdAt: new Date(), isNew: true,
        }, ...prev]);
        okCount++;
      } catch (e) {
        // When responseType: 'blob' is set, error bodies arrive as a Blob, not a parsed object.
        // Read the blob as text then parse JSON to extract the backend's friendly detail message.
        let detail = e?.response?.data?.detail;
        if (!detail && e?.response?.data instanceof Blob) {
          try {
            const text = await e.response.data.text();
            const parsed = JSON.parse(text);
            detail = parsed?.detail;
          } catch { /* fall through to generic message */ }
        }
        // Detail can be a friendly string OR a rich {error, message, byok_help_url} object
        if (detail && typeof detail === "object") {
          if (detail.error === "elevenlabs_payment_issue" || detail.error === "elevenlabs_key_invalid") {
            toast.error(detail.message, {
              duration: 9000,
              action: {
                label: "Connect BYOK",
                onClick: () => navigate("/dashboard/providers"),
              },
            });
            lastErr = null;   // suppress the generic toast after this
            break;
          }
          lastErr = detail.message || detail.error || "Generation failed";
        } else {
          lastErr = detail || e?.message || "Generation failed";
        }
        break;
      }
    }
    setGenerating(false);
    if (okCount > 0) {
      toast.success(`Generated ${okCount} of ${N} take${okCount === 1 ? "" : "s"}`);
      // Clear "new" badge after 4s
      setTimeout(() => setDrops((prev) => prev.map((d) => ({ ...d, isNew: false }))), 4000);
    }
    if (lastErr) toast.error(lastErr);
  };

  // ── Play / Pause ─────────────────────────────────────────────────
  const handlePlay = (drop) => {
    if (playingId === drop.id) {
      try { audioRefs.current[drop.id]?.pause(); } catch { /* noop */ }
      setPlayingId(null);
      return;
    }
    stopAll();
    if (!audioRefs.current[drop.id]) {
      const a = new Audio(drop.audioUrl);
      a.onended = () => setPlayingId(null);
      audioRefs.current[drop.id] = a;
    }
    audioRefs.current[drop.id].play().catch(() => toast.error("Could not play audio"));
    setPlayingId(drop.id);
  };

  // ── Download ────────────────────────────────────────────────────
  const handleDownload = (drop) => {
    const a = document.createElement("a");
    a.href = drop.audioUrl;
    const fileSafe = (stationName || "drop").replace(/\s+/g, "-");
    const voiceSafe = (drop.voiceName || "voice").replace(/\s+/g, "-");
    a.download = `${fileSafe}_${voiceSafe}_drop.mp3`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Download started");
  };

  // ── Add to imaging ──────────────────────────────────────────────
  const addToImaging = async (drop) => {
    try {
      const b64 = await blobToBase64(drop.audioBlob);
      const r = await api.post("/artist-drops/add-to-imaging", {
        name: `${drop.voiceName} — ${dropType} drop`,
        audio_base64: b64,
        duration_sec: drop.durationSec,
        voice_name: drop.voiceName,
        voice_id: drop.voiceId,
        drop_type: dropType,
        script: drop.script,
        format: "mp3",
      });
      if (r.data?.ok) toast.success("Added to Imaging Studio asset library");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to imaging");
    }
  };

  // ── Push to Air ─────────────────────────────────────────────────
  // Persists the drop (if needed) to get a stable asset_id, then POSTs to
  // /api/push-to-air/push which uploads it via the station's configured
  // automation provider (AzuraCast / SFTP / Webhook / Radio.co status).
  const pushToAir = async (drop) => {
    try {
      const b64 = await blobToBase64(drop.audioBlob);
      const r = await api.post("/artist-drops/add-to-imaging", {
        name: `${drop.voiceName} — ${dropType} drop`,
        audio_base64: b64,
        duration_sec: drop.durationSec,
        voice_name: drop.voiceName,
        voice_id: drop.voiceId,
        drop_type: dropType,
        script: drop.script,
        format: "mp3",
      });
      const asset_id = r.data?.asset?.asset_id;
      if (!asset_id) throw new Error("Could not persist drop");
      const push = await api.post("/push-to-air/push", {
        asset_id,
        name: `${drop.voiceName} — ${dropType} drop`,
        drop_type: dropType,
      });
      toast.success(push.data?.message || "Pushed to air");
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (!detail && e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail; } catch { /* noop */ }
      }
      const msg = detail || e?.message || "Push to air failed";
      toast.error(msg, {
        description: msg.includes("/dashboard/automation")
          ? "Configure a provider first → Push to Air page in the sidebar."
          : undefined,
      });
    }
  };

  const copyScript = (drop) => {
    navigator.clipboard.writeText(drop.script).then(
      () => toast.success("Script copied"),
      () => toast.error("Clipboard blocked"),
    );
  };

  // ── Send to Production Suite ────────────────────────────────────
  // Persist the drop into imaging_library (so the backend gives us a stable
  // audio_url) then push a "pending clip" envelope into localStorage that
  // the Production Suite reads on mount and drops onto the VO track.
  const sendToProductionSuite = async (drop) => {
    try {
      const b64 = await blobToBase64(drop.audioBlob);
      const r = await api.post("/artist-drops/add-to-imaging", {
        name: `${drop.voiceName} — ${dropType} drop`,
        audio_base64: b64,
        duration_sec: drop.durationSec,
        voice_name: drop.voiceName,
        voice_id: drop.voiceId,
        drop_type: dropType,
        script: drop.script,
        format: "mp3",
      });
      const asset = r.data?.asset || {};
      // audio_url returned by the backend is server-rooted (e.g. /api/artist-drops/asset/X/audio)
      // — prepend REACT_APP_BACKEND_URL so the PS <audio> element fetches cross-origin correctly.
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const audioUrl = (r.data?.audio_url || asset.audio_url || "").startsWith("http")
        ? (r.data?.audio_url || asset.audio_url)
        : `${base}${r.data?.audio_url || asset.audio_url || ""}`;
      const pending = {
        asset_id: asset.asset_id,
        name: asset.label || `${drop.voiceName} drop`,
        audio_url: audioUrl,
        duration_sec: drop.durationSec || asset.duration_sec || 0,
        type: "Voice",
        color: "#4a9eff",
        voice_name: drop.voiceName,
        source: "artist_drop",
        added_at: Date.now(),
      };
      try {
        const existing = JSON.parse(localStorage.getItem("ps_pending_clips") || "[]");
        existing.push(pending);
        localStorage.setItem("ps_pending_clips", JSON.stringify(existing.slice(-12)));
      } catch { /* localStorage blocked — fall through and still navigate */ }
      toast.success("Sent to Production Suite");
      navigate("/dashboard/production-suite");
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (!detail && e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail; } catch { /* noop */ }
      }
      toast.error(detail || "Could not send to Production Suite");
    }
  };

  const allVoiceCards = [
    ...voices,
    { id: "custom", initials: customVoiceId ? "✓" : "+", name: customVoiceId ? (customVoiceName || "Your Station Voice") : "Your Station Voice",
      style: customVoiceId ? "Cloned · Ready" : "Clone your own talent", color: "#4a5068", bg: "#1e2130" },
  ];

  return (
    <div className={s.page} data-testid="artist-drops-page">
      {/* ── Top Bar ─────────────────────────────────────────── */}
      <div className={s.topBar}>
        <div className={s.topLeft}>
          <div className={s.iconSquare}><Mic size={15} /></div>
          <div>
            <div className={s.topTitle}>AI voice drop generator</div>
            <div className={s.topSubtitle}>Generate broadcast-ready station IDs and voice drops</div>
          </div>
        </div>
        <div className={s.topBadge} data-testid="ad-eleven-badge">Powered by ElevenLabs</div>
      </div>

      <div className={s.body}>
        {/* ── LEFT COLUMN ───────────────────────────────────── */}
        <div className={s.left}>
          {/* Section 1 — Script */}
          <div className={s.section}>
            <div className={s.sectionLabel}>// SCRIPT</div>
            <div className={s.tabs}>
              <button type="button" data-testid="ad-tab-templates"
                onClick={() => setScriptTab("templates")}
                className={`${s.tab} ${scriptTab === "templates" ? s.tabActive : ""}`}>Templates</button>
              <button type="button" data-testid="ad-tab-custom"
                onClick={() => setScriptTab("custom")}
                className={`${s.tab} ${scriptTab === "custom" ? s.tabActive : ""}`}>Custom script</button>
            </div>

            {scriptTab === "templates" && (
              <div className={s.tmplGrid} data-testid="ad-template-grid">
                {TEMPLATES.map((t) => (
                  <button type="button" key={t.id}
                    onClick={() => pickTemplate(t)}
                    className={`${s.tmplCard} ${activeTemplate === t.id ? s.tmplCardActive : ""}`}
                    data-testid={`ad-template-${t.id}`}>
                    <div className={s.tmplLabel}>{t.label}</div>
                    <div className={s.tmplPreview}>{t.preview}</div>
                  </button>
                ))}
              </div>
            )}

            <textarea
              className={s.textarea}
              value={script}
              onChange={(e) => setScript(e.target.value.slice(0, 200))}
              placeholder="Write your script — use {VoicePersona} and {Station} as placeholders"
              maxLength={200}
              data-testid="ad-script-textarea"
            />
            <div className={s.charCounter} data-testid="ad-char-counter">{script.length} / 200 characters</div>
            <div
              data-testid="ad-compliance-note"
              style={{
                display: "flex", gap: 8, alignItems: "flex-start",
                marginTop: 10, padding: "9px 11px", borderRadius: 8,
                background: "rgba(245,166,35,0.07)", border: "1px solid rgba(245,166,35,0.3)",
                fontSize: 10.5, lineHeight: 1.55, color: "#c9a86a",
              }}
            >
              <Lightbulb size={12} color="#f5a623" style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <strong style={{ color: "#f5a623" }}>{"{VoicePersona}"}</strong> refers to your station's own
                voice talent or a fictional on-air persona — not a real musician's name. To name-check a real
                artist or track (e.g. "up next, new music from..."), use the script text directly; the generated
                voice itself can't claim to belong to that artist.
              </span>
            </div>
          </div>

          {/* Section 2 — Voice style */}
          <div className={s.section}>
            <div className={s.sectionLabel}>// VOICE STYLE — fictional station personas</div>
            <div className={s.voiceGrid} data-testid="ad-voice-grid">
              {allVoiceCards.map((v) => {
                const isCustom = v.id === "custom";
                const active = selectedVoice === v.id;
                return (
                  <div
                    key={v.id}
                    onClick={() => isCustom ? onPickCustom() : setSelectedVoice(v.id)}
                    className={`${s.voiceCard} ${active ? s.voiceCardActive : ""}`}
                    data-testid={`ad-voice-${v.id}`}
                  >
                    <div className={s.voiceAvatar} style={{ background: v.bg, color: v.color }}>
                      {v.initials}
                    </div>
                    <div className={s.voiceName}>{v.name}</div>
                    <div className={s.voiceStyle}>{v.style}</div>
                    {isCustom ? (
                      <div className={s.customUploadHint} onClick={openReupload} data-testid="ad-custom-upload-hint">
                        <Upload size={9} style={{ display: "inline", marginRight: 3 }} />
                        {customVoiceId ? "Re-upload" : "Upload sample"}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <input ref={cloneInputRef} type="file" accept="audio/*" style={{ display: "none" }}
              onChange={(e) => onCloneFile(e.target.files?.[0])}
              data-testid="ad-clone-input" />
            {cloning && <div style={{ fontSize: 10, color: "#4a9eff", marginTop: 6 }}>
              <Loader2 size={9} className={s.spin} /> Cloning voice…
            </div>}
          </div>

          {/* Section 3 — Settings */}
          <div className={s.section}>
            <div className={s.sectionLabel}>// SETTINGS</div>
            <div className={s.settingsGrid}>
              {["tone", "speed", "musicBed", "format"].map((k) => (
                <div key={k} className={s.settingRow}>
                  <div className={s.settingLabel}>{k === "musicBed" ? "Music bed" : k}</div>
                  <select
                    className={s.select}
                    value={settings[k]}
                    onChange={(e) => setSettings((p) => ({ ...p, [k]: e.target.value }))}
                    data-testid={`ad-setting-${k}`}
                  >
                    {SETTINGS_OPTIONS[k].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <button
            type="button"
            className={s.generateBtn}
            onClick={handleGenerate}
            disabled={generating}
            data-testid="ad-generate-btn"
          >
            {generating
              ? (<><Loader2 size={13} className={s.spin} /> Generating…</>)
              : (<><Sparkles size={13} /> Generate voice drop</>)
            }
          </button>

          {/* Drop history */}
          <div className={s.section} style={{ marginTop: 18 }}>
            <div className={s.sectionLabel}>// GENERATED DROPS</div>
            {drops.length === 0 ? (
              <div className={s.emptyState} data-testid="ad-empty-state">
                No drops yet — hit <strong>Generate voice drop</strong> above to make your first one.
              </div>
            ) : (
              <div className={s.dropList} data-testid="ad-drop-list">
                {drops.map((d) => {
                  const voice = voices.find((v) => v.id === d.voiceId);
                  const heights = waveHeights(d.id, 30);
                  return (
                    <div key={d.id}
                      className={`${s.dropCard} ${d.isNew ? s.dropCardNew : ""}`}
                      data-testid={`ad-drop-${d.id}`}>
                      <div className={s.dropTop}>
                        <div className={s.dropAvatar}
                          style={{ background: voice?.bg || "#1e2130", color: voice?.color || "#4a9eff" }}>
                          {voice?.initials || "C"}
                        </div>
                        <div className={s.dropMeta}>
                          <div className={s.dropMetaName}>{d.voiceName}</div>
                          <div>Generated {timeAgo(d.createdAt)} · {d.duration} · {d.format}</div>
                        </div>
                        {d.isNew && <div className={s.newBadge}>New</div>}
                      </div>

                      <div className={s.waveform}>
                        {heights.map((h, i) => (
                          <div key={i}
                            className={`${s.waveBar} ${playingId === d.id ? s.waveBarPlaying : ""}`}
                            style={{ height: `${h}%`, animationDelay: `${i * 30}ms` }} />
                        ))}
                      </div>

                      <div className={s.scriptPreview}>{d.script}</div>

                      <div className={s.dropActions}>
                        <button type="button" onClick={() => handlePlay(d)}
                          className={`${s.actionBtn} ${s.actionBtnAccent}`}
                          data-testid={`ad-drop-play-${d.id}`}>
                          {playingId === d.id ? <Pause size={10} /> : <Play size={10} />}
                          {playingId === d.id ? "Pause" : "Play"}
                        </button>
                        <button type="button" onClick={() => handleDownload(d)}
                          className={s.actionBtn}
                          data-testid={`ad-drop-download-${d.id}`}>
                          <Download size={10} /> Download
                        </button>
                        <button type="button" onClick={() => addToImaging(d)}
                          className={s.actionBtn}
                          data-testid={`ad-drop-imaging-${d.id}`}>
                          <Volume2 size={10} /> Add to imaging
                        </button>
                        <button type="button" onClick={() => sendToProductionSuite(d)}
                          className={s.actionBtn}
                          data-testid={`ad-drop-send-to-ps-${d.id}`}>
                          <SlidersHorizontal size={10} /> Send to PS
                        </button>
                        <button type="button" onClick={() => pushToAir(d)}
                          className={`${s.actionBtn} ${s.actionBtnAccent}`}
                          data-testid={`ad-drop-push-${d.id}`}>
                          <RadioIcon size={10} /> Push to Air
                        </button>
                        <button type="button" onClick={() => copyScript(d)}
                          className={s.actionBtn}
                          data-testid={`ad-drop-copy-${d.id}`}>
                          <CopyIcon size={10} /> Copy script
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL ───────────────────────────────────── */}
        <div className={s.right}>
          <div className={s.section}>
            <div className={s.sectionLabel}>Voice Persona Name</div>
            <input className={s.input}
              value={personaName}
              onChange={(e) => setPersonaName(e.target.value)}
              placeholder="e.g. Marcus J., DJ Ace, your talent's name"
              data-testid="ad-artist-input"
            />
            <div style={{ fontSize: 9.5, lineHeight: 1.5, color: "#4a5068", marginTop: 6 }} data-testid="ad-persona-hint">
              This is the name spoken in the drop. Use your own on-air talent or a fictional persona — not a real musician's name or likeness.
            </div>
          </div>

          <div className={s.section}>
            <div className={s.sectionLabel}>Station name</div>
            <input className={s.input}
              value={stationName}
              onChange={(e) => setStationName(e.target.value)}
              data-testid="ad-station-input"
            />
          </div>

          <div className={s.section}>
            <div className={s.sectionLabel}>Drop type</div>
            {DROP_TYPES.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.id}
                  onClick={() => setDropType(d.id)}
                  className={`${s.dropTypeRow} ${dropType === d.id ? s.dropTypeRowActive : ""}`}
                  data-testid={`ad-droptype-${d.id}`}>
                  <Icon size={12} /> {d.label}
                </div>
              );
            })}
          </div>

          <div className={s.section}>
            <div className={s.sectionLabel}>Variations</div>
            <div style={{ fontSize: 9, color: "#4a5068", marginBottom: 5 }}>Generate multiple takes</div>
            <div className={s.variations}>
              {[3, 5, 10].map((n) => (
                <button type="button" key={n}
                  onClick={() => setVariations(n)}
                  className={`${s.varPill} ${variations === n ? s.varPillActive : ""}`}
                  data-testid={`ad-var-${n}`}>{n}</button>
              ))}
            </div>
          </div>

          <div className={s.tipsCard} data-testid="ad-tips">
            <div className={s.tipsHeader}><Lightbulb size={11} color="#f59e0b" /> Pro tips</div>
            {TIPS.map((t, i) => (
              <div key={i} className={s.tip}>
                <span className={s.tipBold}>{t.bold}</span>{t.rest}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Consent attestation dialog (voice cloning) ─────────── */}
      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent className="bg-bg-surface border-line text-ink-primary max-w-md" data-testid="ad-consent-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Clone Your Station Voice</DialogTitle>
            <DialogDescription className="text-ink-secondary text-xs leading-relaxed">
              Upload a 30s+ sample of your own on-air talent (with their consent) to create an AI clone
              for station IDs, sweepers, and promos. Cloning a real musician or any person without their
              written consent is not permitted.
            </DialogDescription>
          </DialogHeader>
          <label
            className="flex items-start gap-2.5 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 cursor-pointer"
            data-testid="ad-consent-label"
          >
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#f5a623]"
              data-testid="ad-consent-checkbox"
            />
            <span className="text-xs text-ink-secondary leading-relaxed">
              I confirm this is my own voice, or I have explicit written consent from the person whose
              voice this is, to create an AI clone of it.
            </span>
          </label>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConsentOpen(false)}
              data-testid="ad-consent-cancel"
              className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-xs text-ink-secondary hover:text-ink-primary transition"
            >Cancel</button>
            <button
              type="button"
              disabled={!consentChecked}
              onClick={() => { setConsentOpen(false); setShowCloneRecorder(true); }}
              data-testid="ad-consent-record"
              className="rounded-md border border-rose-500/50 bg-rose-500/10 px-4 py-2 text-xs font-bold text-rose-300 hover:bg-rose-500/20 disabled:opacity-40 transition"
            >Record sample now</button>
            <button
              type="button"
              disabled={!consentChecked}
              onClick={() => { setConsentOpen(false); cloneInputRef.current?.click(); }}
              data-testid="ad-consent-continue"
              className="rounded-md bg-brand-amber px-4 py-2 text-xs font-bold text-black disabled:opacity-40 transition"
            >Choose audio sample</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {showCloneRecorder && (
        <VoiceRecorderModal
          title="Record voice sample (30s or more)"
          onCancel={() => setShowCloneRecorder(false)}
          onUse={(file) => { setShowCloneRecorder(false); onCloneFile(file); }}
        />
      )}
    </div>
  );
}
